import { Link } from 'react-router-dom';
import Navigation from '../../components/feature/Navigation';
import Footer from '../../components/feature/Footer';

export default function AthleticTurfPage() {
  return (
    <div className="min-h-screen bg-white">
      <Navigation />

      {/* Hero Section */}
      <section className="pt-32 pb-20 bg-gradient-to-br from-orange-50 to-red-50">
        <div className="max-w-7xl mx-auto px-6">
          <div className="grid md:grid-cols-2 gap-16 items-center">
            <div>
              <span className="inline-block bg-orange-600 text-white text-xs font-bold px-4 py-2 rounded-md mb-6 uppercase tracking-wide">Athletic Turf</span>
              <h1 className="text-6xl font-bold text-gray-900 mb-6 leading-tight">Professional Sports Field Turf</h1>
              <p className="text-xl text-gray-700 mb-8 leading-relaxed">
                High-performance synthetic turf for sports fields, training facilities, and athletic complexes in Tulsa. Built to withstand intense play while providing superior safety and performance.
              </p>
              <div className="flex flex-wrap gap-5">
                <a href="tel:918-806-8873" className="bg-orange-600 text-white px-8 py-4 rounded-full text-base font-semibold hover:bg-orange-700 transition-all hover:scale-105 whitespace-nowrap">
                  Get A Quote
                </a>
                <Link to="/about" className="bg-transparent border-2 border-orange-600 text-orange-600 px-8 py-4 rounded-full text-base font-semibold hover:bg-orange-600 hover:text-white transition-all whitespace-nowrap">
                  Learn More
                </Link>
              </div>
            </div>
            <div className="relative">
              <img 
                src="https://public.readdy.ai/ai/img_res/99f744040559a9452e3bb780ac93caf8.jpg" 
                alt="Athletic Turf Tulsa Oklahoma"
                className="rounded-2xl shadow-2xl w-full"
              />
            </div>
          </div>
        </div>
      </section>

      {/* Benefits Section */}
      <section className="py-24">
        <div className="max-w-7xl mx-auto px-6">
          <div className="text-center mb-20">
            <h2 className="text-5xl font-bold text-gray-900 mb-4">Why Choose Athletic Turf</h2>
            <p className="text-xl text-gray-600 max-w-3xl mx-auto">Superior performance, safety, and durability for Tulsa sports facilities</p>
          </div>

          <div className="grid md:grid-cols-3 gap-12">
            <div className="text-center">
              <div className="w-20 h-20 flex items-center justify-center mx-auto mb-7">
                <i className="ri-calendar-check-line text-6xl text-orange-600"></i>
              </div>
              <h3 className="text-2xl font-semibold text-gray-900 mb-4">Year-Round Play</h3>
              <p className="text-base text-gray-600 leading-relaxed">
                Play in any weather condition. No mud, no standing water, no field closures. Your Tulsa sports facility stays operational through rain, heat, and everything in between.
              </p>
            </div>

            <div className="text-center">
              <div className="w-20 h-20 flex items-center justify-center mx-auto mb-7">
                <i className="ri-shield-cross-line text-6xl text-orange-600"></i>
              </div>
              <h3 className="text-2xl font-semibold text-gray-900 mb-4">Enhanced Safety</h3>
              <p className="text-base text-gray-600 leading-relaxed">
                Consistent, level playing surface reduces injury risk. Advanced shock absorption protects athletes during high-impact activities. No divots, holes, or uneven spots.
              </p>
            </div>

            <div className="text-center">
              <div className="w-20 h-20 flex items-center justify-center mx-auto mb-7">
                <i className="ri-money-dollar-circle-line text-6xl text-orange-600"></i>
              </div>
              <h3 className="text-2xl font-semibold text-gray-900 mb-4">Lower Maintenance Costs</h3>
              <p className="text-base text-gray-600 leading-relaxed">
                Eliminate mowing, watering, fertilizing, and reseeding. Reduce maintenance staff time and costs by up to 70%. More budget for programs, less for field upkeep.
              </p>
            </div>

            <div className="text-center">
              <div className="w-20 h-20 flex items-center justify-center mx-auto mb-7">
                <i className="ri-timer-line text-6xl text-orange-600"></i>
              </div>
              <h3 className="text-2xl font-semibold text-gray-900 mb-4">Maximum Field Usage</h3>
              <p className="text-base text-gray-600 leading-relaxed">
                Increase field usage by 3-4x compared to natural grass. Host more games, practices, and events without field degradation. Immediate playability after rain.
              </p>
            </div>

            <div className="text-center">
              <div className="w-20 h-20 flex items-center justify-center mx-auto mb-7">
                <i className="ri-trophy-line text-6xl text-orange-600"></i>
              </div>
              <h3 className="text-2xl font-semibold text-gray-900 mb-4">Professional Performance</h3>
              <p className="text-base text-gray-600 leading-relaxed">
                Consistent ball roll, predictable footing, and optimal traction. Athletes perform at their best on a surface that meets professional standards for speed and playability.
              </p>
            </div>

            <div className="text-center">
              <div className="w-20 h-20 flex items-center justify-center mx-auto mb-7">
                <i className="ri-water-flash-line text-6xl text-orange-600"></i>
              </div>
              <h3 className="text-2xl font-semibold text-gray-900 mb-4">Water Conservation</h3>
              <p className="text-base text-gray-600 leading-relaxed">
                Save millions of gallons of water annually. Critical for Oklahoma's climate and increasingly important for municipal facilities managing water resources.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Sports Applications */}
      <section className="py-24 bg-gray-50">
        <div className="max-w-7xl mx-auto px-6">
          <div className="text-center mb-20">
            <h2 className="text-5xl font-bold text-gray-900 mb-4">Built For Every Sport</h2>
            <p className="text-xl text-gray-600 max-w-3xl mx-auto">Specialized turf systems for different athletic applications</p>
          </div>

          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8" data-product-shop>
            <div className="bg-white rounded-2xl overflow-hidden shadow-lg hover:shadow-2xl transition-shadow">
              <div className="relative h-64 w-full">
                <img 
                  src="https://readdy.ai/api/search-image?query=football%20field%20with%20artificial%20synthetic%20turf%20in%20Tulsa%20Oklahoma%2C%20American%20football%20stadium%20with%20fake%20grass%2C%20yard%20lines%20and%20hash%20marks%2C%20professional%20sports%20field%2C%20clean%20simple%20background&width=600&height=600&seq=football918&orientation=squarish" 
                  alt="Football Field Turf"
                  className="w-full h-full object-cover"
                />
              </div>
              <div className="p-6">
                <h3 className="text-xl font-bold text-gray-900 mb-2">Football Fields</h3>
                <p className="text-sm text-gray-600">High school, college, and professional football surfaces</p>
              </div>
            </div>

            <div className="bg-white rounded-2xl overflow-hidden shadow-lg hover:shadow-2xl transition-shadow">
              <div className="relative h-64 w-full">
                <img 
                  src="https://readdy.ai/api/search-image?query=soccer%20field%20with%20artificial%20synthetic%20turf%20in%20Tulsa%20Oklahoma%2C%20football%20pitch%20with%20fake%20grass%2C%20goal%20posts%20and%20field%20markings%2C%20professional%20soccer%20surface%2C%20clean%20simple%20background&width=600&height=600&seq=soccer918&orientation=squarish" 
                  alt="Soccer Field Turf"
                  className="w-full h-full object-cover"
                />
              </div>
              <div className="p-6">
                <h3 className="text-xl font-bold text-gray-900 mb-2">Soccer Fields</h3>
                <p className="text-sm text-gray-600">FIFA-quality surfaces for competitive soccer</p>
              </div>
            </div>

            <div className="bg-white rounded-2xl overflow-hidden shadow-lg hover:shadow-2xl transition-shadow">
              <div className="relative h-64 w-full">
                <img 
                  src="https://readdy.ai/api/search-image?query=baseball%20field%20with%20artificial%20synthetic%20turf%20infield%20in%20Tulsa%20Oklahoma%2C%20baseball%20diamond%20with%20fake%20grass%2C%20professional%20sports%20surface%2C%20clean%20simple%20background&width=600&height=600&seq=baseball918&orientation=squarish" 
                  alt="Baseball Field Turf"
                  className="w-full h-full object-cover"
                />
              </div>
              <div className="p-6">
                <h3 className="text-xl font-bold text-gray-900 mb-2">Baseball &amp; Softball</h3>
                <p className="text-sm text-gray-600">Infield and outfield turf systems</p>
              </div>
            </div>

            <div className="bg-white rounded-2xl overflow-hidden shadow-lg hover:shadow-2xl transition-shadow">
              <div className="relative h-64 w-full">
                <img 
                  src="https://readdy.ai/api/search-image?query=multi-sport%20athletic%20field%20with%20artificial%20synthetic%20turf%20in%20Tulsa%20Oklahoma%2C%20versatile%20sports%20surface%20with%20multiple%20field%20markings%2C%20recreational%20facility%2C%20clean%20simple%20background&width=600&height=600&seq=multisport918&orientation=squarish" 
                  alt="Multi-Sport Field Turf"
                  className="w-full h-full object-cover"
                />
              </div>
              <div className="p-6">
                <h3 className="text-xl font-bold text-gray-900 mb-2">Multi-Sport Fields</h3>
                <p className="text-sm text-gray-600">Versatile surfaces for multiple sports</p>
              </div>
            </div>

            <div className="bg-white rounded-2xl overflow-hidden shadow-lg hover:shadow-2xl transition-shadow">
              <div className="relative h-64 w-full">
                <img 
                  src="https://readdy.ai/api/search-image?query=training%20facility%20with%20artificial%20synthetic%20turf%20in%20Tulsa%20Oklahoma%2C%20indoor%20sports%20training%20center%20with%20fake%20grass%2C%20athletic%20performance%20area%2C%20clean%20simple%20background&width=600&height=600&seq=training918&orientation=squarish" 
                  alt="Training Facility Turf"
                  className="w-full h-full object-cover"
                />
              </div>
              <div className="p-6">
                <h3 className="text-xl font-bold text-gray-900 mb-2">Training Facilities</h3>
                <p className="text-sm text-gray-600">Indoor and outdoor training centers</p>
              </div>
            </div>

            <div className="bg-white rounded-2xl overflow-hidden shadow-lg hover:shadow-2xl transition-shadow">
              <div className="relative h-64 w-full">
                <img 
                  src="https://readdy.ai/api/search-image?query=track%20and%20field%20facility%20with%20artificial%20synthetic%20turf%20in%20Tulsa%20Oklahoma%2C%20athletic%20track%20with%20fake%20grass%20infield%2C%20sports%20complex%2C%20clean%20simple%20background&width=600&height=600&seq=track918&orientation=squarish" 
                  alt="Track Infield Turf"
                  className="w-full h-full object-cover"
                />
              </div>
              <div className="p-6">
                <h3 className="text-xl font-bold text-gray-900 mb-2">Track Infields</h3>
                <p className="text-sm text-gray-600">Synthetic turf for track and field events</p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Technical Features */}
      <section className="py-24">
        <div className="max-w-7xl mx-auto px-6">
          <div className="grid md:grid-cols-2 gap-16 items-center">
            <div className="relative">
              <img 
                src="https://readdy.ai/api/search-image?query=close-up%20of%20professional%20athletic%20artificial%20synthetic%20turf%20fibers%2C%20high-performance%20sports%20grass%20material%2C%20durable%20fake%20grass%20for%20athletics%2C%20detailed%20texture%20of%20sports%20field%20turf%2C%20clean%20simple%20background&width=800&height=900&seq=athleticdetail918&orientation=portrait" 
                alt="Athletic Turf Technology"
                className="rounded-2xl shadow-2xl w-full"
              />
            </div>
            <div>
              <h2 className="text-5xl font-bold text-gray-900 mb-6">Advanced Athletic Technology</h2>
              <div className="space-y-6">
                <div className="flex gap-4">
                  <div className="flex-shrink-0">
                    <i className="ri-checkbox-circle-fill text-3xl text-orange-600"></i>
                  </div>
                  <div>
                    <h3 className="text-xl font-bold text-gray-900 mb-2">Shock Absorption System</h3>
                    <p className="text-base text-gray-600 leading-relaxed">
                      Multi-layer construction with engineered pad systems reduces impact forces by up to 40%. Protects athletes during high-impact activities and reduces injury risk.
                    </p>
                  </div>
                </div>

                <div className="flex gap-4">
                  <div className="flex-shrink-0">
                    <i className="ri-checkbox-circle-fill text-3xl text-orange-600"></i>
                  </div>
                  <div>
                    <h3 className="text-xl font-bold text-gray-900 mb-2">Optimal Traction</h3>
                    <p className="text-base text-gray-600 leading-relaxed">
                      Engineered fiber design and infill system provide consistent traction for cutting, pivoting, and acceleration. Reduces slipping while allowing natural movement.
                    </p>
                  </div>
                </div>

                <div className="flex gap-4">
                  <div className="flex-shrink-0">
                    <i className="ri-checkbox-circle-fill text-3xl text-orange-600"></i>
                  </div>
                  <div>
                    <h3 className="text-xl font-bold text-gray-900 mb-2">Superior Drainage</h3>
                    <p className="text-base text-gray-600 leading-relaxed">
                      Perforated backing and drainage layer remove water at 30+ inches per hour. Fields are playable within minutes after heavy rain—no more cancelled games.
                    </p>
                  </div>
                </div>

                <div className="flex gap-4">
                  <div className="flex-shrink-0">
                    <i className="ri-checkbox-circle-fill text-3xl text-orange-600"></i>
                  </div>
                  <div>
                    <h3 className="text-xl font-bold text-gray-900 mb-2">UV Stabilization</h3>
                    <p className="text-base text-gray-600 leading-relaxed">
                      Advanced UV inhibitors protect against Oklahoma's intense sun. Maintains color, strength, and performance characteristics for 10+ years of heavy use.
                    </p>
                  </div>
                </div>

                <div className="flex gap-4">
                  <div className="flex-shrink-0">
                    <i className="ri-checkbox-circle-fill text-3xl text-orange-600"></i>
                  </div>
                  <div>
                    <h3 className="text-xl font-bold text-gray-900 mb-2">Temperature Management</h3>
                    <p className="text-base text-gray-600 leading-relaxed">
                      Specialized fibers and infill options help regulate surface temperature. Stays cooler than standard turf during Tulsa's hot summer months.
                    </p>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* ROI Section */}
      <section className="py-24 bg-gradient-to-br from-orange-50 to-red-50">
        <div className="max-w-7xl mx-auto px-6">
          <div className="text-center mb-16">
            <h2 className="text-5xl font-bold text-gray-900 mb-4">Smart Investment For Tulsa Facilities</h2>
            <p className="text-xl text-gray-600 max-w-3xl mx-auto">Athletic turf pays for itself through reduced maintenance and increased usage</p>
          </div>

          <div className="grid md:grid-cols-2 gap-12">
            <div className="bg-white p-10 rounded-2xl shadow-lg">
              <h3 className="text-3xl font-bold text-gray-900 mb-6">Cost Savings</h3>
              <ul className="space-y-4">
                <li className="flex items-start gap-3">
                  <i className="ri-checkbox-circle-fill text-2xl text-orange-600 flex-shrink-0 mt-1"></i>
                  <span className="text-base text-gray-700">Eliminate water bills—save thousands annually on irrigation</span>
                </li>
                <li className="flex items-start gap-3">
                  <i className="ri-checkbox-circle-fill text-2xl text-orange-600 flex-shrink-0 mt-1"></i>
                  <span className="text-base text-gray-700">No mowing, fertilizing, or pesticide applications</span>
                </li>
                <li className="flex items-start gap-3">
                  <i className="ri-checkbox-circle-fill text-2xl text-orange-600 flex-shrink-0 mt-1"></i>
                  <span className="text-base text-gray-700">Reduce maintenance staff time by 70%</span>
                </li>
                <li className="flex items-start gap-3">
                  <i className="ri-checkbox-circle-fill text-2xl text-orange-600 flex-shrink-0 mt-1"></i>
                  <span className="text-base text-gray-700">No field renovation or reseeding costs</span>
                </li>
                <li className="flex items-start gap-3">
                  <i className="ri-checkbox-circle-fill text-2xl text-orange-600 flex-shrink-0 mt-1"></i>
                  <span className="text-base text-gray-700">Lower equipment and fuel expenses</span>
                </li>
              </ul>
            </div>

            <div className="bg-white p-10 rounded-2xl shadow-lg">
              <h3 className="text-3xl font-bold text-gray-900 mb-6">Revenue Opportunities</h3>
              <ul className="space-y-4">
                <li className="flex items-start gap-3">
                  <i className="ri-checkbox-circle-fill text-2xl text-orange-600 flex-shrink-0 mt-1"></i>
                  <span className="text-base text-gray-700">Host more games and tournaments—increase rental revenue</span>
                </li>
                <li className="flex items-start gap-3">
                  <i className="ri-checkbox-circle-fill text-2xl text-orange-600 flex-shrink-0 mt-1"></i>
                  <span className="text-base text-gray-700">Expand programming with 3-4x more field availability</span>
                </li>
                <li className="flex items-start gap-3">
                  <i className="ri-checkbox-circle-fill text-2xl text-orange-600 flex-shrink-0 mt-1"></i>
                  <span className="text-base text-gray-700">Attract premier events with professional-quality surface</span>
                </li>
                <li className="flex items-start gap-3">
                  <i className="ri-checkbox-circle-fill text-2xl text-orange-600 flex-shrink-0 mt-1"></i>
                  <span className="text-base text-gray-700">Year-round usage—no seasonal downtime</span>
                </li>
                <li className="flex items-start gap-3">
                  <i className="ri-checkbox-circle-fill text-2xl text-orange-600 flex-shrink-0 mt-1"></i>
                  <span className="text-base text-gray-700">Enhanced facility reputation and community value</span>
                </li>
              </ul>
            </div>
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="py-24 bg-gradient-to-br from-orange-600 to-red-700">
        <div className="max-w-5xl mx-auto px-6 text-center">
          <h2 className="text-5xl md:text-6xl font-bold text-white mb-6 leading-tight">Upgrade Your Tulsa Sports Facility</h2>
          <p className="text-xl text-white/90 mb-10 max-w-3xl mx-auto leading-relaxed">
            Get a free consultation and quote for athletic turf. We'll help you design the perfect field for your facility and budget.
          </p>
          <div className="flex flex-wrap justify-center gap-5">
            <a href="tel:918-806-8873" className="bg-white text-orange-600 px-10 py-5 rounded-full text-lg font-bold hover:bg-gray-100 transition-all hover:scale-105 whitespace-nowrap">
              Call 918-806-8873
            </a>
            <Link to="/about" className="bg-transparent border-2 border-white text-white px-10 py-5 rounded-full text-lg font-bold hover:bg-white hover:text-orange-600 transition-all whitespace-nowrap">
              Learn More
            </Link>
          </div>
        </div>
      </section>

      <Footer />
    </div>
  );
}